import { css } from '@emotion/react';
import { Box, styled } from '@mui/material';
import { getRouteApi, useMatch, useNavigate } from '@tanstack/react-router';
import type { LngLatBoundsLike, ViewStateChangeEvent } from '@vis.gl/react-maplibre';
import { AttributionControl, Map } from '@vis.gl/react-maplibre';
import { Suspense, useCallback } from 'react';

import '@/assets/css/maplibre-gl.css';

import logo from '@/assets/logos/parken-in-hessen.svg?url';
import { ParkAndRideFacilityPopup } from '@/components/map/details/popup/ParkAndRideFacilityPopup';
import { ParkingFacilityPopup } from '@/components/map/details/popup/ParkingFacilityPopup';
import { PickupParkingFacilityPopup } from '@/components/map/details/popup/PickupParkingFacilityPopup';
import { StationPopup } from '@/components/map/details/popup/StationPopup';
import { MapLayers } from '@/components/map/layers/MapLayers';
import { MaskingLayer } from '@/components/map/layers/MaskingLayer';
import { MenuBar } from '@/components/navigations/MenuBar';
import { MAP_FLY_TO_ZOOM } from '@/modules/MAP_FLY_TO_ZOOM';
import { MAP_MAX_ZOOM } from '@/modules/MAP_MAX_ZOOM';
import { useMapStore } from '@/stores/useMapStore';
import { FeatureType } from '@/types/FeatureType';
import { MapStyle } from '@/types/MapStyle';

const route = getRouteApi('/_public/_map');

export const BOUNDS = [7.772, 49.396, 10.236, 51.659] as LngLatBoundsLike;

const LogoWrapper = styled(Box)(
  ({ theme }) => css`
    position: absolute;
    top: ${theme.spacing(1)};
    left: 50%;
    transform: translateX(-50%);
    display: none;
    background-color: rgba(255, 255, 255, 0.75);
    backdrop-filter: blur(8px);

    ${theme.breakpoints.up('lg')} {
      display: flex;

      img {
        width: ${theme.spacing(24)};
        padding: ${theme.spacing(1.5)};
      }
    }
  `,
);

export function PublicMap() {
  const navigate = useNavigate();

  const { id, type, laenge: longitude, breite: latitude, zoom } = route.useSearch();
  const style = useMapStore((state) => state.style);

  const initialViewState =
    longitude && latitude ? { longitude, latitude, zoom: zoom ?? MAP_FLY_TO_ZOOM } : { bounds: BOUNDS };

  const match = useMatch({ from: '/_public/_map' });

  const Popup =
    !!id &&
    !!type &&
    {
      [FeatureType.PARKING_FACILITY]: ParkingFacilityPopup,
      [FeatureType.PARK_AND_RIDE_FACILITY]: ParkAndRideFacilityPopup,
      [FeatureType.STATION]: StationPopup,
      [FeatureType.PICKUP_PARKING_FACILITY]: PickupParkingFacilityPopup,
    }[type];

  const handleMoveEnd = useCallback(
    async (event: ViewStateChangeEvent) =>
      navigate({
        to: '.',
        search: (prev) => ({
          ...prev,
          laenge: parseFloat(event.viewState.longitude.toFixed(6)),
          breite: parseFloat(event.viewState.latitude.toFixed(6)),
          zoom: parseFloat(event.viewState.zoom.toFixed(1)),
        }),
      }),
    [navigate],
  );

  return (
    <Map
      attributionControl={false}
      bearing={0}
      dragRotate={false}
      id="map"
      initialViewState={initialViewState}
      maxZoom={MAP_MAX_ZOOM}
      minZoom={5}
      onMoveEnd={handleMoveEnd}
      pitch={0}
      touchPitch={false}
      mapStyle={
        {
          [MapStyle.BASIC]: 'https://sgx.geodatenzentrum.de/gdz_basemapde_vektor/styles/bm_web_gry.json',
          [MapStyle.SATELLITE]: '/api/map-style/satellite.json',
        }[style]
      }
    >
      {match ? (
        <LogoWrapper>
          <img alt="Parken in Hessen" src={logo} />
        </LogoWrapper>
      ) : null}
      <MenuBar />

      <Suspense>{Popup ? <Popup id={id} /> : null}</Suspense>

      <AttributionControl compact position="bottom-left" />

      <MaskingLayer />

      <MapLayers />
    </Map>
  );
}
